import { useState } from 'react'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardBody } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { SkeletonRows } from '@/components/ui/Skeleton'
import { useProfile } from '@/auth/SessionProvider'
import { listPeople } from '@/data/admin'
import { listDepartments } from '@/data/departments'
import { listHistory } from '@/data/history'
import type { HistoryEntry, HistoryFilter } from '@/data/history'
import { formatMoment, RECORD_KINDS } from '@/lib/auditText'

const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Please try again.')

/** Kinds of record that belong to a department (the rest are church-wide and never in a department's history). */
const DEPARTMENT_KINDS = [
  'contribution', 'expense', 'budget', 'budget_line', 'dues_plan', 'dues_enrollment', 'dues_charge', 'dues_allocation',
  'department_membership', 'project', 'collection_sheet', 'cash_movement', 'cash_movement_match', 'reconciliation_exception',
  'attachment', 'user_role', 'department',
]

const VERB_TONE = { INSERT: 'success', UPDATE: 'info', DELETE: 'danger' } as const

/**
 * Who added, changed or deleted what, and when. With a department: that department's own trail (anyone who
 * may open the department). Without: the whole church (administrators and auditors). The database decides
 * which rows each person may read; this screen only asks.
 */
export function HistoryPage({ deptId }: { deptId?: string }) {
  const profile = useProfile()
  const churchWide = !deptId
  const [filter, setFilter] = useState<HistoryFilter>({})
  const effective: HistoryFilter = { ...filter, departmentId: deptId ?? filter.departmentId }

  const depts = useQuery({ queryKey: ['departments'], queryFn: () => listDepartments() })
  const people = useQuery({ queryKey: ['people'], queryFn: listPeople, enabled: churchWide && profile.isChurchReader })
  const history = useInfiniteQuery({
    queryKey: ['history', effective],
    queryFn: ({ pageParam }) => listHistory(effective, pageParam),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
  })
  const entries = history.data?.pages.flatMap((p) => p.entries) ?? []
  const deptName = (id: string | null) => (id ? depts.data?.find((d) => d.id === id)?.name : undefined)

  const kinds = Object.entries(RECORD_KINDS)
    .filter(([t]) => churchWide || DEPARTMENT_KINDS.includes(t))
    .sort((a, b) => a[1].localeCompare(b[1]))
  const set = (patch: Partial<HistoryFilter>) => setFilter((f) => ({ ...f, ...patch }))
  const filtered = Boolean(filter.table || filter.actorId || filter.from || filter.to || filter.departmentId)

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-title font-semibold text-ink">History</h2>
        <p className="mt-1 text-muted">
          {churchWide
            ? 'Every record added, changed or deleted anywhere in the church, by whom and when.'
            : `Everything added, changed or deleted in ${deptName(deptId) ?? 'this department'}, by whom and when.`}{' '}
          Nobody can edit or remove this history.
        </p>
      </div>

      <Card>
        <CardBody>
          <div className={`grid gap-3 sm:grid-cols-2 ${churchWide ? 'lg:grid-cols-5' : 'lg:grid-cols-3'}`}>
            {churchWide && (
              <Select
                label="Department"
                value={filter.departmentId ?? ''}
                onChange={(e) => set({ departmentId: e.target.value || undefined })}
                options={[{ value: '', label: 'All' }, ...(depts.data ?? []).map((d) => ({ value: d.id, label: d.name }))]}
              />
            )}
            <Select
              label="Kind of record"
              value={filter.table ?? ''}
              onChange={(e) => set({ table: e.target.value || undefined })}
              options={[{ value: '', label: 'All' }, ...kinds.map(([t, k]) => ({ value: t, label: k.charAt(0).toUpperCase() + k.slice(1) }))]}
            />
            {churchWide && (
              <Select
                label="Done by"
                value={filter.actorId ?? ''}
                onChange={(e) => set({ actorId: e.target.value || undefined })}
                options={[{ value: '', label: 'Anyone' }, ...(people.data ?? []).map((p) => ({ value: p.id, label: p.fullName }))]}
              />
            )}
            <Input label="From" type="date" value={filter.from ?? ''} max={filter.to} onChange={(e) => set({ from: e.target.value || undefined })} />
            <Input label="To" type="date" value={filter.to ?? ''} min={filter.from} onChange={(e) => set({ to: e.target.value || undefined })} />
          </div>
          {filtered && (
            <Button variant="secondary" size="sm" className="mt-3" onClick={() => setFilter({})}>Clear filters</Button>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          {history.isPending ? (
            <SkeletonRows rows={4} />
          ) : history.isError ? (
            <div>
              <p role="alert" className="text-body text-bad">{errText(history.error)}</p>
              <Button variant="secondary" size="lg" className="mt-3" onClick={() => void history.refetch()}>Try again</Button>
            </div>
          ) : entries.length === 0 ? (
            <EmptyState
              title={filtered ? 'Nothing matches' : 'No history yet'}
              description={filtered ? 'Try a wider date range or clear the filters.' : 'Changes appear here as soon as anyone records something.'}
            />
          ) : (
            <>
              <ul className="divide-y divide-line" aria-label="History">
                {entries.map((e) => (
                  <Entry key={e.id} e={e} deptLabel={churchWide ? deptName(e.departmentId) : undefined} />
                ))}
              </ul>
              {history.hasNextPage && (
                <Button variant="secondary" size="lg" className="mt-4 w-full sm:w-auto" isLoading={history.isFetchingNextPage} onClick={() => void history.fetchNextPage()}>
                  Show older
                </Button>
              )}
              {history.isFetchNextPageError && <p role="alert" className="mt-2 text-body text-bad">Could not load the older history. Try again.</p>}
            </>
          )}
        </CardBody>
      </Card>
    </div>
  )
}

function Entry({ e, deptLabel }: { e: HistoryEntry; deptLabel?: string }) {
  const details = e.changes.map((c) => (
    <li key={c.field} className="break-words">
      <span className="text-muted">{c.field}:</span>{' '}
      {c.before === null && c.after === null ? (
        <span className="text-ink">changed</span>
      ) : e.op === 'UPDATE' ? (
        <span className="text-ink">
          <span className="line-through decoration-muted">{c.before}</span> <span aria-hidden="true">→</span>
          <span className="sr-only">to</span> {c.after}
        </span>
      ) : (
        <span className="text-ink">{c.after ?? c.before}</span>
      )}
    </li>
  ))

  return (
    <li className="py-3">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <Badge tone={VERB_TONE[e.op]}>{e.verb.charAt(0).toUpperCase() + e.verb.slice(1)}</Badge>
        <p className="text-ink">
          <span className="font-medium">{e.who}</span> {e.verb} {e.op === 'INSERT' ? (/^[aeiou]/i.test(e.kind) ? 'an' : 'a') : 'the'} {e.kind}
          {e.label && <span className="font-medium">: {e.label}</span>}
        </p>
      </div>
      <p className="mt-0.5 text-body text-muted">
        {formatMoment(e.at)}
        {deptLabel && ` · ${deptLabel}`}
      </p>
      {details.length > 0 &&
        (e.op === 'UPDATE' ? (
          <ul className="mt-1 text-body">{details}</ul>
        ) : (
          <details className="mt-1 text-body">
            <summary className="cursor-pointer text-brand">Show details</summary>
            <ul className="mt-1">{details}</ul>
          </details>
        ))}
    </li>
  )
}
